/**
 * Build an `ImportPlan` from a HangarMax scene.
 *
 * Pure and synchronous: no network, no Supabase, no clock. Everything variable
 * (aircraft type catalogue, existing links, location centre, "now") arrives via
 * `PlanContext`, which is what makes the plan reviewable in the UI before a
 * single row is written and testable without a database.
 */

import { hashObject } from './hash'
import {
  classifyObject,
  findSourceDeleted,
  indexLinks,
  linkKey,
} from './diff'
import { buildingDisplayName, mapBuildingGeometry } from './mapBuildings'
import {
  buildAircraftTypeIndex,
  matchAircraftType,
} from './matchAircraftTypes'
import {
  classifyItem,
  itemDimensionsFeet,
  itemDisplayLabel,
  obstacleGeometry,
  resolveHost,
  type HostBuildingFrame,
} from './mapItems'
import {
  anchorOffsetFeet,
  hmSceneToPmSceneFeet,
  itemRotationFromHmRotation,
  sceneFeetToLatLng,
} from './transform'
import type {
  CatalogSource,
  HmSceneData,
  ImportCounts,
  ImportPlan,
  ImportSourceMeta,
  PlanContext,
  PlannedAircraft,
  PlannedBuilding,
  PlannedObstacle,
  PlannedSkip,
  PlannedSourceDeleted,
} from './types'

/** `ObstacleRenderer` allocates instanced meshes with this cap. */
const OBSTACLE_INSTANCE_CAP = 200

export function buildImportPlan(params: {
  sceneData: HmSceneData
  catalog: CatalogSource
  meta: ImportSourceMeta
  ctx: PlanContext
}): ImportPlan {
  const { sceneData, catalog, meta, ctx } = params

  const warnings: string[] = []
  const sceneHash = hashObject(sceneData)
  const sceneUnchanged = ctx.previousSceneHash === sceneHash

  const linksByKey = indexLinks(ctx.existingLinks)
  const seenKeys = new Set<string>()
  const targetExists = (id: string | null): boolean =>
    !id || !ctx.existingTargetIds || ctx.existingTargetIds.has(id)

  const anchorOffset = anchorOffsetFeet(sceneData.anchor, ctx.locationCenter)

  // --------------------------------------------------------------------------
  // Buildings
  // --------------------------------------------------------------------------
  const buildings: PlannedBuilding[] = []
  const hosts: HostBuildingFrame[] = []

  ;(sceneData.buildings ?? []).forEach((building, index) => {
    const key = linkKey('building', building.id)
    seenKeys.add(key)

    const geometry = mapBuildingGeometry(building)
    const sceneCenterFt = hmSceneToPmSceneFeet(anchorOffset, {
      x: building.scene_position?.x ?? 0,
      z: building.scene_position?.z ?? 0,
    })
    const { lat, lng } = sceneFeetToLatLng(sceneCenterFt, ctx.locationCenter)

    const link = linksByKey.get(key)
    const sourceHash = hashObject(building)
    const outcome = classifyObject({
      link,
      sourceHash,
      targetExists: targetExists(link?.targetId ?? null),
      mappable: true,
    })

    const fidelityNotes = [...geometry.fidelityNotes]
    if (outcome.targetMissing) {
      fidelityNotes.push('Previously imported building no longer exists in PlaneMAX — it will be recreated.')
    }

    const name = buildingDisplayName(building, index)
    const refs = (link?.targetRefs ?? {}) as { unit_ids?: unknown }

    buildings.push({
      kind: 'building',
      op: outcome.op,
      sourceObjectId: building.id,
      sourceHash,
      raw: building,
      linkId: outcome.linkId,
      label: name,
      fidelityNotes,
      approximate: geometry.approximate,
      targetAreaId: outcome.targetMissing ? null : link?.targetId ?? null,
      targetUnitIds: Array.isArray(refs.unit_ids) ? (refs.unit_ids as string[]) : [],
      area: {
        name,
        areaType: 'single_unit_hangar',
        templateType: 'single_unit_community',
        latitude: lat,
        longitude: lng,
        rotationDeg: geometry.rotationDeg,
        widthFt: round2(geometry.widthFt),
        depthFt: round2(geometry.depthFt),
        heightFt: round2(geometry.heightFt),
        doors: geometry.doors,
        floorColor: geometry.floorColor,
        wallColor: geometry.wallColor,
        sceneCenterFt,
      },
    })

    // The single-unit template emits exactly one unit spanning the footprint,
    // at offset (0, 0) with no Z centring.
    hosts.push({
      buildingSourceId: building.id,
      frame: {
        centerFt: sceneCenterFt,
        rotationDeg: geometry.rotationDeg,
        widthFt: round2(geometry.widthFt),
        depthFt: round2(geometry.depthFt),
      },
      centerZShiftFt: 0,
      units: [
        {
          index: 0,
          offsetX: 0,
          offsetY: 0,
          widthFt: round2(geometry.widthFt),
          depthFt: round2(geometry.depthFt),
        },
      ],
    })
  })

  // --------------------------------------------------------------------------
  // Items
  // --------------------------------------------------------------------------
  const aircraftIndex = buildAircraftTypeIndex(ctx.aircraftTypes)
  const aircraft: PlannedAircraft[] = []
  const obstacles: PlannedObstacle[] = []
  const skipped: PlannedSkip[] = []

  let tempTailSeq = 0

  ;(sceneData.items ?? []).forEach((item, index) => {
    const key = linkKey('item', item.id)
    seenKeys.add(key)

    const link = linksByKey.get(key)
    const sourceHash = hashObject(item)
    const entry = catalog.get(item.catalog_id)
    const label = itemDisplayLabel(item, entry, index)
    const dims = itemDimensionsFeet(item, entry)
    const position = hmSceneToPmSceneFeet(anchorOffset, item.position)
    const hmRotation = item.rotation ?? 0

    const classification = classifyItem(item, entry)

    if (classification.kind === 'skip') {
      skipped.push(
        makeSkip({ item, sourceHash, link, label, reason: classification.reason })
      )
      return
    }

    if (classification.kind === 'aircraft') {
      const match = matchAircraftType(entry, item.label ?? null, aircraftIndex)
      const outcome = classifyObject({
        link,
        sourceHash,
        targetExists: targetExists(link?.targetId ?? null),
        mappable: true,
      })

      const notes: string[] = []
      if (!match.aircraftTypeId) {
        notes.push(
          'No PlaneMAX aircraft type matched — imported with HangarMax dimensions and rendered procedurally.'
        )
      } else if (match.method === 'dims') {
        notes.push('Matched by dimensions only — verify the aircraft type.')
      }
      if (outcome.targetMissing) {
        notes.push('Previously imported aircraft no longer exists in PlaneMAX — it will be recreated.')
      }

      const refs = (link?.targetRefs ?? {}) as { allocation_id?: unknown }
      const isCreate = outcome.op === 'create'

      aircraft.push({
        kind: 'aircraft',
        op: outcome.op,
        sourceObjectId: item.id,
        sourceHash,
        raw: item,
        linkId: outcome.linkId,
        label,
        fidelityNotes: notes,
        catalogId: item.catalog_id ?? null,
        match,
        wingspanFt: dims.widthFt,
        lengthFt: dims.lengthFt,
        heightFt: dims.heightFt,
        manufacturer: entry?.manufacturer ?? null,
        model: entry?.name ?? null,
        position,
        rotationDeg: itemRotationFromHmRotation(hmRotation),
        host: resolveHost(position, hosts, item.parent_building)?.ref ?? null,
        targetAircraftId: outcome.targetMissing ? null : link?.targetId ?? null,
        targetAllocationId:
          !outcome.targetMissing && typeof refs.allocation_id === 'string'
            ? refs.allocation_id
            : null,
        tailNumber: isCreate ? tempTailNumber(ctx.now, tempTailSeq++) : null,
      })
      return
    }

    // Obstacle — requires a host unit (obstacles.unit_id is NOT NULL).
    const host = resolveHost(position, hosts, item.parent_building)
    if (!host) {
      skipped.push(
        makeSkip({
          item,
          sourceHash,
          link,
          label,
          reason:
            'Outside every imported building — PlaneMAX obstacles must belong to a unit.',
        })
      )
      return
    }

    const outcome = classifyObject({
      link,
      sourceHash,
      targetExists: targetExists(link?.targetId ?? null),
      mappable: true,
    })

    const geometry = obstacleGeometry({
      host,
      dims,
      shape: classification.shape,
      hmRotationDeg: hmRotation,
    })

    const notes: string[] = []
    if (item.label && item.type !== 'aircraft') {
      // obstacles do have a name column, so the label survives here
      notes.push('Imported as an obstacle — HangarMax colour and label preserved where PlaneMAX supports them.')
    }
    if (outcome.targetMissing) {
      notes.push('Previously imported obstacle no longer exists in PlaneMAX — it will be recreated.')
    }

    obstacles.push({
      kind: 'obstacle',
      op: outcome.op,
      sourceObjectId: item.id,
      sourceHash,
      raw: item,
      linkId: outcome.linkId,
      label,
      fidelityNotes: notes,
      catalogId: item.catalog_id ?? null,
      host: host.ref,
      name: label,
      obstacleType: classification.obstacleType,
      shape: classification.shape,
      positionX: geometry.positionX,
      positionY: geometry.positionY,
      rotation: geometry.rotation,
      widthFt: geometry.widthFt,
      depthFt: geometry.depthFt,
      radiusFt: geometry.radiusFt,
      heightFt: geometry.heightFt,
      color: classification.color,
      targetObstacleId: outcome.targetMissing ? null : link?.targetId ?? null,
    })
  })

  // --------------------------------------------------------------------------
  // Source deletions + counts
  // --------------------------------------------------------------------------
  const sourceDeleted: PlannedSourceDeleted[] = findSourceDeleted(
    ctx.existingLinks,
    seenKeys
  ).map((link) => ({
    kind: 'source_deleted',
    op: 'source_deleted',
    linkId: link.id,
    sourceObjectId: link.sourceObjectId,
    sourceObjectType: link.sourceObjectType,
    label: `${link.sourceObjectType === 'building' ? 'Building' : 'Item'} ${link.sourceObjectId}`,
    targetTable: link.targetTable,
    targetId: link.targetId,
  }))

  const writes = [...buildings, ...aircraft, ...obstacles]
  const counts: ImportCounts = {
    created: writes.filter((w) => w.op === 'create').length,
    updated: writes.filter((w) => w.op === 'update').length,
    unchanged: writes.filter((w) => w.op === 'noop').length,
    unmatched: skipped.length,
    sourceDeleted: sourceDeleted.length,
  }

  if (obstacles.length > OBSTACLE_INSTANCE_CAP) {
    warnings.push(
      `${obstacles.length} obstacles exceed the ${OBSTACLE_INSTANCE_CAP}-instance rendering cap — some will not be drawn.`
    )
  }
  if (!catalog.hasNames && aircraft.length > 0) {
    warnings.push(
      'The HangarMax catalog manifest was unavailable, so aircraft can only be matched by size. Re-run the import when it is reachable for better type matches.'
    )
  }
  if (buildings.length === 0 && (sceneData.buildings?.length ?? 0) > 0) {
    warnings.push('No buildings could be mapped from this layout.')
  }
  if (sceneData.flythroughs) {
    warnings.push('Flythrough paths are preserved but not imported — PlaneMAX has no camera-path feature yet.')
  }
  if (sceneData.airportCode) {
    warnings.push(
      `Source layout is tagged ${sceneData.airportCode}; the PlaneMAX location's own coordinates are used as the scene origin.`
    )
  }

  return {
    meta,
    sceneHash,
    sceneUnchanged,
    anchor: sceneData.anchor,
    buildings,
    aircraft,
    obstacles,
    skipped,
    sourceDeleted,
    warnings,
    counts,
  }
}

function makeSkip(params: {
  item: { id: string; catalog_id?: string | null }
  sourceHash: string
  link: { id: string } | undefined
  label: string
  reason: string
}): PlannedSkip {
  return {
    kind: 'skip',
    op: 'unmatched',
    sourceObjectId: params.item.id,
    sourceHash: params.sourceHash,
    raw: params.item,
    linkId: params.link?.id ?? null,
    label: params.label,
    fidelityNotes: [],
    catalogId: params.item.catalog_id ?? null,
    reason: params.reason,
  }
}

/**
 * TEMP tail number, matching `addPlaneWizardStore.generateTempTailNumber`'s
 * prefix (so `isTempTailNumber` recognises it) plus a per-batch sequence, since
 * a whole layout imports inside the same second.
 */
export function tempTailNumber(now: Date, sequence: number): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
  return `TEMP_${stamp}_${String(sequence + 1).padStart(3, '0')}`
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}
